import Vue from 'vue';
import { inject, provide } from 'vue-demi';
import { useAxios } from '@teamwork/use';
import { v1Url } from '@teamwork/fetcher';
import useStore from './useStore';
import { usePreferences } from '@/platform/composables/usePreferences';
import { useSidebarPinnedItems } from '@/platform/composables/useSidebarPinnedItems';
import { useFeatures } from '@/platform/composables/useFeatures';
import { useTeamworkVersion } from '@/platform/composables/useTeamworkVersion';
import { useLightspeedBridge } from '@/platform/composables/useLightspeedBridge';

const versionSwitcherSymbol = Symbol('useVersionSwitcher');

export function provideVersionSwitcher() {
  const store = useStore();
  const axios = useAxios();
  const {
    shouldShowPersonalizedExperience,
    shouldShowSwitchEveryoneModal,
    shouldShowRedesignedSidebar,
    hasTriedRedesignedSidebar,
  } = usePreferences();
  const { pinMultipleItems, setPinnedPresets } = useSidebarPinnedItems();
  const { lightspeedEnabled } = useFeatures();
  const { redirectToLightspeed, redirectToTWA, teamworkVersion } =
    useTeamworkVersion();
  const { isInLightspeed } = useLightspeedBridge();

  const showSwitchToClassicModal = () => {
    store.dispatch('modals/open', {
      name: 'switch-back-to-classic-modal',
    });
  };

  const submitClassicSwitchFeedback = async ({ feedbackText }) => {
    try {
      await axios.post('/feedback.json', {
        feedback: {
          source: 'Teamwork Projects',
          summary: 'Switching back to Classic Side Navigation Feedback',
          message: feedbackText,
          type: 'Switch back to Classic Side Navigation',
          sendACopy: false,
          urgency: 'Low',
          fileurl: '',
          beatingType: '',
          pendingFileAttachments: [],
          updateFiles: true,
          attachments: '',
          removeOtherFiles: true,
        },
      });

      return true;
    } catch (error) {
      store.dispatch('notifications/flashes/error', {
        title: Vue.t('Oops. There was an error submitting your feedback'),
        msg: Vue.t('Please try again or contact support@teamwork.com'),
      });
      return false;
    }
  };
  const switchToClassicUI = async () => {
    if (isInLightspeed.value) {
      // Updating outside of `usePreferences` as we don't want any side-effects
      await axios.put(v1Url('me/prefs'), {
        prefs: { shouldShowRedesignedSidebar: '' },
      });
      await axios.put(v1Url('me/prefs'), {
        prefs: {
          shouldShowRedesignedSidebar: false,
          hasTriedRedesignedSidebar: true,
        },
      });
      teamworkVersion.value = 'twa';
      redirectToTWA();
    } else {
      shouldShowRedesignedSidebar.value = false;
      hasTriedRedesignedSidebar.value = true;
    }
  };

  const batchUpdateMyPreferences = async (
    presets = [],
    withPersonalizedExperience = true,
  ) => {
    // Updating outside of `usePreferences` as we don't want any side-effects
    await axios.put(v1Url('me/prefs'), {
      prefs: {
        shouldShowRedesignedSidebar: '',
        shouldShowPersonalizedExperience: '',
      },
    });

    // only set presets if they are passed in
    const sidebarPinnedItems = presets.length
      ? { sidebarPinnedItems: setPinnedPresets(presets) }
      : {};

    await axios.put(v1Url('me/prefs'), {
      prefs: {
        shouldShowPersonalizedExperience: withPersonalizedExperience,
        shouldShowRedesignedSidebar: true,
        hasTriedRedesignedSidebar: true,
        ...sidebarPinnedItems,
      },
    });
  };

  const switchToNewUIForMe = async (
    presets = [],
    withPersonalizedExperience = true,
  ) => {
    if (lightspeedEnabled.value) {
      await batchUpdateMyPreferences(presets, withPersonalizedExperience);

      // switch only user
      store.dispatch(
        'notifications/flashes/success',
        Vue.t('You successfully switched to New Teamwork'),
      );

      teamworkVersion.value = 'lightspeed';
      redirectToLightspeed();
    } else {
      shouldShowRedesignedSidebar.value = true;
      hasTriedRedesignedSidebar.value = true;
      shouldShowPersonalizedExperience.value = withPersonalizedExperience;
      pinMultipleItems(presets);
    }
  };

  const switchToNewUIForEveryone = async (
    presets = [],
    withPersonalizedExperience = true,
  ) => {
    await store.dispatch('preferences/persist', {
      prefs: { switchAllUsersToRedesignedSidebar: true },
      scope: 'account',
    });

    store.dispatch(
      'notifications/flashes/success',
      Vue.t(
        'Everyone in your company is successfully switched to New Teamwork',
      ),
    );

    if (lightspeedEnabled.value) {
      await batchUpdateMyPreferences(presets, withPersonalizedExperience);
      teamworkVersion.value = 'lightspeed';
      redirectToLightspeed();
    } else {
      shouldShowSwitchEveryoneModal.value = false;
      shouldShowRedesignedSidebar.value = true;
      hasTriedRedesignedSidebar.value = true;
      shouldShowPersonalizedExperience.value = withPersonalizedExperience;
      pinMultipleItems(presets);
    }
  };

  provide(versionSwitcherSymbol, {
    submitClassicSwitchFeedback,
    showSwitchToClassicModal,
    switchToClassicUI,
    switchToNewUIForEveryone,
    switchToNewUIForMe,
  });
}

export function useVersionSwitcher() {
  return inject(versionSwitcherSymbol);
}
